import { seti18n } from './locale'
import { fetchDeliverableTypes } from './deliverables'
import { fetchCustomers } from './users'
import {
  fetchFieldTypes,
  fetchIrrigationTypes,
  fetchFieldIds,
  fetchFilteredFields,
  fetchGeometries,
  fetchCustomerValues,
  fetchCutNumberValues,
  fetchFieldTypesValues,
  fetchSeasonLabelValues
} from './fields'

import { receiveAllData, receiveAllDashboardData } from './viewmode'

import { setFilteredFieldIds, fetchGlobalMapCentroids, fetchGlobalLayerByLegend, fetchGlobalLayerByDeliverableName } from './globalLayer'

import userStoredData from '../userStoredData'
import { fetchSampleTypes } from './sampleTypes'
import { fetchEox } from './eox'
import { setRequestStart, setRequestEnd } from './ui'
import { fetchFieldSeasonParameters } from './seasons'
import { fetchFieldsTileOrder } from './fieldsTileOrder'
import { fetchReportTypes, fetchReportStatuses } from './reports'
import { fetchCustomersDeliverables } from './customersDeliverables'

import { isUIRequestDashboardDataInitializationStarted, isUIRequestViewerDataInitializationSuccess } from '../selectors/ui'
import { getFieldCount } from '../selectors/fieldsStatistics'

export const initializeLocale = () => seti18n(userStoredData.locale)
export const REQUEST_DASHBOARD_DATA_INITIALIZATION = 'REQUEST_DASHBOARD_DATA_INITIALIZATION'
export const REQUEST_VIEWER_DATA_INITIALIZATION = 'REQUEST_VIEWER_DATA_INITIALIZATION'

// The field ids give the search its count and chunks. Once stored, the other page's initialization must not
// ask again: a filter applied meanwhile stored its own (fetchFilteredFields), which the full list would
// overwrite. A count of 0 is a count too (no fields, or a filter that matches none).
const hasFieldCount = state => getFieldCount(state) !== undefined

export const initializeViewer = () => async (dispatch, getState) => {
  dispatch(setRequestStart(REQUEST_VIEWER_DATA_INITIALIZATION))

  // Read before the EOX wait: on a cold start the search bar's first query (fetchFilteredFields) may store a count
  // meanwhile, and the viewer still asks for its own ids then.
  const isFieldCountKnown = hasFieldCount(getState())

  try {
    await dispatch(fetchEox())
  } catch (error) {
    console.log("Couldn't fetch EOX capabilities")
  }

  await initializeViewerData(dispatch, isFieldCountKnown)

  dispatch(receiveAllData())
  dispatch(setRequestEnd(REQUEST_VIEWER_DATA_INITIALIZATION))
}

export const initializeViewerGeometries = () => dispatch => {
  dispatch(fetchGeometries())
}

const initializeViewerData = async (dispatch, isFieldCountKnown) => {
  const fetchInitialData = [dispatch(fetchDeliverableTypes()), dispatch(fetchCustomersDeliverables()), !isFieldCountKnown && dispatch(fetchFieldIds())]

  const fetchData = [
    dispatch(fetchFieldTypes()),
    dispatch(fetchCustomers()),
    dispatch(fetchIrrigationTypes()),
    dispatch(fetchReportTypes()),
    dispatch(fetchReportStatuses()),
    dispatch(fetchFieldSeasonParameters()),
    dispatch(fetchFieldsTileOrder()),
    dispatch(fetchCustomerValues()),
    dispatch(fetchCutNumberValues()),
    dispatch(fetchFieldTypesValues()),
    dispatch(fetchSeasonLabelValues()),
    dispatch(fetchSampleTypes())
  ]

  await Promise.all(fetchInitialData)
  return await Promise.all(fetchData)
}

// The dashboard loads its own list, not the viewer's. Once: a remount while it loads must not start it again.
export const initializeDashboard = () => async (dispatch, getState) => {
  if (isUIRequestDashboardDataInitializationStarted(getState())) return

  dispatch(setRequestStart(REQUEST_DASHBOARD_DATA_INITIALIZATION))

  // Coming from the viewer, its data already holds the whole list: ready at once, without a blank page. A
  // viewer still loading is not waited for (one failed request stops it for good): the dashboard loads its own.
  if (!isUIRequestViewerDataInitializationSuccess(getState())) {
    await initializeDashboardData(dispatch, hasFieldCount(getState()))
  }

  dispatch(receiveAllDashboardData())
  dispatch(setRequestEnd(REQUEST_DASHBOARD_DATA_INITIALIZATION))
}

// What the dashboard shows: none of the viewer's EOX, sample types, irrigation types or season parameters.
// The tile order is in it: a field opened from the dashboard asks for its images with it (PLANET, S2DR).
// Settled, not all: each request reports its own failure, and one must not leave the page blank.
const initializeDashboardData = async (dispatch, isFieldCountKnown) => {
  // First, as in the viewer: deliverable types and customers deliverables go into the seasons of the fields received.
  const fetchInitialData = [dispatch(fetchDeliverableTypes()), dispatch(fetchCustomersDeliverables()), !isFieldCountKnown && dispatch(fetchFieldIds())]

  await Promise.allSettled(fetchInitialData)

  return Promise.allSettled([
    dispatch(fetchFieldTypes()),
    dispatch(fetchCustomers()),
    dispatch(fetchReportTypes()),
    dispatch(fetchReportStatuses()),
    dispatch(fetchFieldsTileOrder()),
    dispatch(fetchCustomerValues()),
    dispatch(fetchCutNumberValues()),
    dispatch(fetchFieldTypesValues()),
    dispatch(fetchSeasonLabelValues())
  ])
}

export const initializeGlobalLayers = () => dispatch => {
  dispatch(fetchGlobalLayerByDeliverableName())
  dispatch(fetchGlobalLayerByLegend())
  dispatch(fetchGlobalMapCentroids())
}

export const initializeSearchFilter = () => dispatch => {
  dispatch(setFilteredFieldIds())
  dispatch(fetchFilteredFields())
}
